/**
 * src/game/weapons/registry.ts — 主武器 id → behavior factory + metadata。
 *
 * scene 啟動依 progress.selectedWeapon 從這裡取對應 behavior(未解鎖/未知 → 回退
 * 預設 rapid-cannon,保證永遠有得打)。metadata(名稱/emoji/一句話玩法)之後 P2 UI
 * 用來畫選擇/升級畫面。
 *
 * 鐵則:此層只組裝 behavior,不碰 progress 寫入、不碰修煉值。
 */

import type { WeaponBehavior } from './types';
import { RapidCannonBehavior, RAPID_CANNON_ID, RAPID_CANNON_BLURB } from './rapid-cannon';
import { RailgunBehavior, RAILGUN_ID, RAILGUN_BLURB } from './railgun';
import { HomingMissileBehavior, HOMING_MISSILE_ID, HOMING_MISSILE_BLURB } from './homing-missile';
import { SpinBladeBehavior, SPIN_BLADE_ID, SPIN_BLADE_BLURB } from './spin-blade';
import { LaserBeamBehavior, LASER_BEAM_ID, LASER_BEAM_BLURB } from './laser-beam';
import { MortarBehavior, MORTAR_ID, MORTAR_BLURB } from './mortar';

/** 預設主武器 id(progress.selectedWeapon 缺省 / 未解鎖時回退到它)。 */
export const DEFAULT_WEAPON_ID = RAPID_CANNON_ID;

/**
 * 一把武器的「展示用數值」(純資料,給選武器 UI 一眼看出差別)。
 * 鐵則:純展示,不參與任何傷害/間隔計算 (那些走各 behavior 的 *_DAMAGE_MULT / 間隔)。
 * 星級 1~5 反映各武器實際定位:
 *   - power    攻擊力:單發/單 tick 的傷害輕重 (對應 DAMAGE_MULT × 命中數)。
 *   - fireRate 射速:出手頻率 (對應發射間隔,越短星越高)。
 *   - range    範圍:打得到多遠/多廣 (整排 lane / AoE / 自動追蹤 = 高;近戰 = 低)。
 * special = 一句話特色 (最突出的玩法標記,K-3 看得懂)。
 */
export interface WeaponStats {
  /** 攻擊力 1~5 星。 */
  power: number;
  /** 射速 1~5 星。 */
  fireRate: number;
  /** 範圍 1~5 星。 */
  range: number;
  /** 特效一句話 (最突出的特色)。 */
  special: string;
}

/** 一把主武器的展示用 metadata(P2 選擇/升級 UI、圖鑑用)。 */
export interface WeaponMeta {
  id: string;
  displayName: string;
  emoji: string;
  /** 一句話玩法說明(K-3 看得懂)。 */
  blurb: string;
  /** 展示用數值(攻擊力/射速/範圍 星級 + 特效一句話)。純資料,不入戰鬥計算。 */
  stats: WeaponStats;
}

/** 一筆 registry 紀錄:metadata + behavior 工廠(每場 new 一個,跨場不殘留)。 */
export interface WeaponRegistryEntry extends WeaponMeta {
  create: () => WeaponBehavior;
}

/** id → registry entry。新增武器在此註冊一行即可。 */
export const WEAPON_REGISTRY: Readonly<Record<string, WeaponRegistryEntry>> = {
  // 連發機砲:低傷但射速最快、好上手。特色 = 穩定連射 (基本款)。
  [RAPID_CANNON_ID]: {
    id: RAPID_CANNON_ID,
    displayName: '連發機砲',
    emoji: '🔫',
    blurb: RAPID_CANNON_BLURB,
    stats: { power: 2, fireRate: 5, range: 3, special: '穩定連射,好上手' },
    create: () => new RapidCannonBehavior(),
  },
  // 軌道炮:傷害最高 (6×) 但慢、蓄力放整排 lane。特色 = 蓄力穿透整排。
  [RAILGUN_ID]: {
    id: RAILGUN_ID,
    displayName: '軌道炮',
    emoji: '⚡',
    blurb: RAILGUN_BLURB,
    stats: { power: 5, fireRate: 1, range: 5, special: '蓄力後一道光束穿透整排' },
    create: () => new RailgunBehavior(),
  },
  // 追蹤飛彈:中傷慢射、自動追最近敵人 (射程=全場)。特色 = 射後不理、自動追蹤。
  [HOMING_MISSILE_ID]: {
    id: HOMING_MISSILE_ID,
    displayName: '追蹤飛彈',
    emoji: '🚀',
    blurb: HOMING_MISSILE_BLURB,
    stats: { power: 3, fireRate: 2, range: 5, special: '飛彈自動追最近敵人' },
    create: () => new HomingMissileBehavior(),
  },
  // 旋刃:近戰環刃、無子彈、要貼身。特色 = 近戰、範圍最小但持續刮。
  [SPIN_BLADE_ID]: {
    id: SPIN_BLADE_ID,
    displayName: '旋刃',
    emoji: '🌀',
    blurb: SPIN_BLADE_BLURB,
    stats: { power: 2, fireRate: 4, range: 1, special: '近戰環刃,衝進敵群刮血' },
    create: () => new SpinBladeBehavior(),
  },
  // 雷射光束:傷害最低但高頻連續掃描、會過熱、窄 lane。特色 = 連續掃描、移動瞄準。
  [LASER_BEAM_ID]: {
    id: LASER_BEAM_ID,
    displayName: '雷射光束',
    emoji: '🔆',
    blurb: LASER_BEAM_BLURB,
    stats: { power: 1, fireRate: 5, range: 2, special: '連續掃描,放久會過熱' },
    create: () => new LaserBeamBehavior(),
  },
  // 榴彈炮:高傷 (3.2×) 慢射、拋物落地 AoE 炸一圈。特色 = 範圍 AoE 潑群。
  [MORTAR_ID]: {
    id: MORTAR_ID,
    displayName: '榴彈炮',
    emoji: '💥',
    blurb: MORTAR_BLURB,
    stats: { power: 4, fireRate: 1, range: 4, special: '拋物落地炸一圈 (範圍傷害)' },
    create: () => new MortarBehavior(),
  },
};

/** registry 內所有武器 metadata(圖鑑/選單列表用)。 */
export const ALL_WEAPON_META: readonly WeaponMeta[] = Object.values(WEAPON_REGISTRY).map(
  ({ id, displayName, emoji, blurb, stats }) => ({ id, displayName, emoji, blurb, stats }),
);

/**
 * 解析「該裝哪把武器的 behavior」。
 * - selectedWeapon 在 registry 內 **且** 在 unlockedWeapons 內 → 用它。
 * - 否則(未選 / 未知 id / 未解鎖)→ 回退預設 rapid-cannon。
 * rapid-cannon 視為「永遠已解鎖」(預設武器),不需出現在 unlockedWeapons。
 *
 * 純函式;回傳新建的 behavior 實例(每場 new,跨場不殘留狀態)。
 */
export function resolveWeaponBehavior(
  selectedWeapon: string | null | undefined,
  unlockedWeapons: readonly string[] | null | undefined,
): WeaponBehavior {
  const id = resolveWeaponId(selectedWeapon, unlockedWeapons);
  return WEAPON_REGISTRY[id].create();
}

/**
 * 解析「該裝哪把武器的 id」(不建實例,給 UI / 測試查)。規則同上。
 */
export function resolveWeaponId(
  selectedWeapon: string | null | undefined,
  unlockedWeapons: readonly string[] | null | undefined,
): string {
  if (!selectedWeapon || !(selectedWeapon in WEAPON_REGISTRY)) {
    return DEFAULT_WEAPON_ID;
  }
  if (selectedWeapon === DEFAULT_WEAPON_ID) return DEFAULT_WEAPON_ID;
  const unlocked = Array.isArray(unlockedWeapons) ? unlockedWeapons : [];
  return unlocked.includes(selectedWeapon) ? selectedWeapon : DEFAULT_WEAPON_ID;
}

/** 查一把武器的 metadata(找不到回 undefined)。 */
export function weaponMetaById(id: string): WeaponMeta | undefined {
  const e = WEAPON_REGISTRY[id];
  return e ? { id: e.id, displayName: e.displayName, emoji: e.emoji, blurb: e.blurb, stats: e.stats } : undefined;
}
